import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import FeatureGate from "@/components/FeatureGate";
import { useAuth } from "@/contexts/AuthContext";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { InventoryStepper } from "@/components/inventory/InventoryStepper";
import { TakeInventoryPanel } from "@/components/inventory/TakeInventoryPanel";
import { ManageItemListPanel } from "@/components/inventory/ManageItemListPanel";
import { InvoicesReceiptsPanel } from "@/components/inventory/InvoicesReceiptsPanel";
import { MatchToMenuPanel } from "@/components/inventory/MatchToMenuPanel";
import { ReviewApprovePanel } from "@/components/inventory/ReviewApprovePanel";
import { ShoppingListPanel } from "@/components/inventory/ShoppingListPanel";
import { MealSettingsPanel } from "@/components/inventory/MealSettingsPanel";
import { StoragePhotosPanel } from "@/components/inventory/StoragePhotosPanel";
import { InventoryAddonCheckout } from "@/components/inventory/InventoryAddonCheckout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function InventoryShoppingListPage() {
  const { user, token } = useAuth();
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const isOwner = user?.role === "owner" || user?.role?.toLowerCase() === "co-owner";

  const [activeStep, setActiveStep] = useState("match-to-menu");
  const [takeInventoryTab, setTakeInventoryTab] = useState("inventory");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const hasLoadedItemsRef = useRef(false);
  const [accessError, setAccessError] = useState(null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [storageAreas, setStorageAreas] = useState([
    { key: "freezer", label: "Freezer", isDefault: true },
    { key: "pantry", label: "Pantry", isDefault: true },
    { key: "refrigerator", label: "Refrigerator", isDefault: true },
    { key: "unassigned", label: "Unassigned", isDefault: true },
  ]);
  const [purchaseRefreshKey, setPurchaseRefreshKey] = useState(0);

  const fetchStorageAreas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inventory/storage-areas`, { headers: authHeaders });
      if (res.data.areas) setStorageAreas(res.data.areas);
    } catch (err) {
      console.error("Error fetching storage areas:", err);
    }
  }, [authHeaders]);

  const fetchItems = useCallback(async () => {
    // Only show the full-panel loader on the very first load. A later refresh (e.g. after
    // "Add Item") must not unmount the step panel below, or it loses its own local state
    // (like which storage-area tab is open).
    if (!hasLoadedItemsRef.current) setLoadingItems(true);
    try {
      const res = await axios.get(`${API}/inventory/items`, { headers: authHeaders });
      setItems(res.data.items || []);
      setAccessError(null);
      setAccessStatus(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402 || status === 403) {
        setAccessError(err.response.data?.detail || "Access to this feature is currently unavailable.");
        setAccessStatus(status);
      } else {
        console.error("Error fetching inventory items:", err);
      }
    } finally {
      setLoadingItems(false);
      hasLoadedItemsRef.current = true;
    }
  }, [authHeaders]);

  useEffect(() => { if (token) { fetchItems(); fetchStorageAreas(); } }, [token, fetchItems, fetchStorageAreas]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("addon") === "success") {
      toast.success("Payment received! Activating your add-on…");
      window.history.replaceState({}, "", window.location.pathname);
      if (token) setTimeout(() => fetchItems(), 2000);
    }
    // eslint-disable-line
  }, [token]);

  const navigateMonth = (direction) => {
    let m = month + direction;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  return (
    <AppShell pageTitle="Menu, Inventory & Shopping List">
      <FeatureGate feature="inventory_shopping_list">
        <div data-testid="inventory-shopping-list-page" className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4 print:hidden">
            <div>
              <h1 className="text-3xl font-bold text-slate-800">Menu, Inventory & Shopping List</h1>
              <p className="text-slate-500 mt-1">
                Count what you have, match it to your menu, and get a smart shopping list.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {!accessError && <InventoryAddonCheckout authHeaders={authHeaders} onRefresh={fetchItems} compact />}
              {!accessError && (
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
                  <button data-testid="inventory-prev-month" onClick={() => navigateMonth(-1)}>
                    <ChevronLeft className="w-4 h-4 text-slate-500" />
                  </button>
                  <span data-testid="inventory-current-month" className="text-sm font-semibold text-slate-700 min-w-[110px] text-center">
                    {MONTHS[month - 1]} {year}
                  </span>
                  <button data-testid="inventory-next-month" onClick={() => navigateMonth(1)}>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {loadingItems ? (
            <p className="text-sm text-slate-400 py-6 text-center">Loading...</p>
          ) : accessError ? (
            <div data-testid="inventory-access-locked" className="flex flex-col items-center text-center gap-3 border border-slate-200 bg-slate-50 rounded-xl py-14 px-6">
              <Lock className="w-8 h-8 text-slate-400" />
              <p className="text-slate-700 font-medium max-w-md">{accessError}</p>
              {accessStatus === 402 ? (
                <InventoryAddonCheckout authHeaders={authHeaders} onRefresh={fetchItems} />
              ) : accessStatus === 403 ? (
                <p className="text-sm text-slate-500 max-w-md">
                  Ask your Owner or Director for Co-Owner or Business Manager access to use this area.
                </p>
              ) : (
                <p className="text-sm text-slate-500 max-w-md">
                  Contact Today's Kidz support to add this feature to your plan.
                </p>
              )}
            </div>
          ) : (
            <>
              <InventoryStepper activeStep={activeStep} onStepClick={setActiveStep} />
              {activeStep === "take-inventory" && (
                <Tabs value={takeInventoryTab} onValueChange={setTakeInventoryTab}>
                  <TabsList data-testid="take-inventory-tabs">
                    <TabsTrigger value="inventory" data-testid="tab-inventory" className="font-semibold text-slate-600 data-[state=active]:text-foreground">This Month's Menu Items</TabsTrigger>
                    <TabsTrigger value="manage-items" data-testid="tab-manage-items" className="font-semibold text-slate-600 data-[state=active]:text-foreground">Master Item List</TabsTrigger>
                    <TabsTrigger value="invoices-receipts" data-testid="tab-invoices-receipts" className="font-semibold text-slate-600 data-[state=active]:text-foreground">Invoices & Receipts</TabsTrigger>
                  </TabsList>
                  <TabsContent value="inventory" className="mt-4">
                    <TakeInventoryPanel month={month} year={year} authHeaders={authHeaders} storageAreas={storageAreas}
                      isOwner={isOwner} onAreasChanged={fetchStorageAreas} items={items} onItemsChanged={fetchItems}
                      refreshKey={purchaseRefreshKey} />
                  </TabsContent>
                  <TabsContent value="manage-items" className="mt-4">
                    <ManageItemListPanel items={items} authHeaders={authHeaders} onRefresh={fetchItems} isOwner={isOwner}
                      storageAreas={storageAreas} month={month} year={year} purchaseRefreshKey={purchaseRefreshKey} />
                  </TabsContent>
                  <TabsContent value="invoices-receipts" className="mt-4">
                    <InvoicesReceiptsPanel month={month} year={year} authHeaders={authHeaders} items={items}
                      storageAreas={storageAreas} onPurchaseAdded={() => setPurchaseRefreshKey((k) => k + 1)} />
                  </TabsContent>
                </Tabs>
              )}

              {activeStep === "match-to-menu" && (
                <MatchToMenuPanel month={month} year={year} authHeaders={authHeaders} items={items} onItemsChanged={fetchItems} storageAreas={storageAreas}
                  onManageItems={() => { setActiveStep("take-inventory"); setTakeInventoryTab("manage-items"); }} />
              )}

              {activeStep === "review-approve" && (
                <ReviewApprovePanel month={month} year={year} authHeaders={authHeaders} isOwner={isOwner} storageAreas={storageAreas} />
              )}

              {activeStep === "shopping-list" && (
                <ShoppingListPanel month={month} year={year} authHeaders={authHeaders} />
              )}

              {activeStep === "meal-settings" && (
                <MealSettingsPanel authHeaders={authHeaders} isOwner={isOwner} />
              )}

              {activeStep === "photos" && (
                <StoragePhotosPanel month={month} year={year} authHeaders={authHeaders} storageAreas={storageAreas} />
              )}
            </>
          )}
        </div>
      </FeatureGate>
    </AppShell>
  );
}
